<template>
  <div class="chatbot-adapter" :class="{ 'is-ai-chatbot': useAiChatbot }">
    <div
      v-if="useAiChatbot"
      ref="aiChatbotRoot"
      class="ai-chatbot-embed-root"
    ></div>
    <LegacyChatbot
      v-else
      ref="legacyChatbot"
      :has-referral-icon="hasReferralIcon"
      :show-referral="showReferral"
      :generate-user-id="generateUserId"
      :show-chatbot-message-box="showChatbotMessageBox"
      :company="company"
      :job-id="jobId"
      :context="context"
      :henkel-wechat-account-open-id="henkelWechatAccountOpenId"
      :btn-dialog-toggle-bootem="btnDialogToggleBootem"
      :btn-dialog-toggle-right="btnDialogToggleRight"
      :enable-close-btn-to-dialog="enableCloseBtnToDialog"
      :clickable="clickable"
      :nudge-bottom="nudgeBottom"
      @applyJob="handleApplyJob"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LegacyChatbot from './LegacyChatbot.vue'
import { buildAiChatbotEmbedOptions, getAiChatbotEmbedApi, loadAiChatbotEmbedScript } from './aiChatbotEmbed'
import { ACTION_TRIGGER_EVENT_APPLY_JOB } from '@/constant/aiChatbot'

const STAY_TRIGGER_DELAY_MS = 30 * 1000

export default {
  components: {
    LegacyChatbot
  },
  props: {
    hasReferralIcon: {
      type: Boolean,
      default: null
    },
    showReferral: {
      type: Boolean,
      default: null
    },
    generateUserId: {
      required: true
    },
    showChatbotMessageBox: {
      type: Boolean,
      default: null
    },
    company: {
      type: Object,
      required: true
    },
    jobId: {
      type: String,
      default: ''
    },
    context: {
      type: Object,
      default: () => {
        return {}
      }
    },
    henkelWechatAccountOpenId: {
      type: String,
      default: ''
    },
    btnDialogToggleBootem: {
      type: String,
      default: ''
    },
    btnDialogToggleRight: {
      type: String,
      default: ''
    },
    enableCloseBtnToDialog: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: true
    },
    nudgeBottom: {
      type: String,
      default: null
    },
    currentAccountId: {
      default: null
    },
    useAiChatbot: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      systemPromptOverride: null,
      aiChatbotInstance: null,
      aiChatbotAvailability: 'idle',
      aiChatbotUnavailableReason: null,
      aiChatbotMountRequestToken: 0,
      aiChatbotPublicConfig: null,
      aiChatbotStay30sTimer: null,
      aiChatbotTriggerRequestToken: 0
    }
  },
  computed: {
    ...mapGetters('aiChatbot', ['aiChatbotVisitorMap']),
    aiChatbotCompanyId () {
      const companyId = this.company?.root_company?.id
      return companyId ? `${companyId}` : ''
    },
    aiChatbotVisitorId () {
      return this.aiChatbotVisitorMap[this.aiChatbotCompanyId]
    },
    aiChatbotEmbedOptions () {
      return buildAiChatbotEmbedOptions({
        company: this.company,
        companyId: this.aiChatbotCompanyId,
        publicConfig: this.aiChatbotPublicConfig,
        jobId: this.jobId,
        userId: this.currentAccountId,
        visitorId: this.aiChatbotVisitorId,
        locale: this.$i18n && this.$i18n.locale
      })
    },
    aiChatbotEmbedOptionsKey () {
      return JSON.stringify(this.aiChatbotEmbedOptions)
    },
    hasRequiredAiChatbotIdentity () {
      return Boolean(this.aiChatbotEmbedOptions.companyId)
    },
    aiChatbotTriggerContextKey () {
      return JSON.stringify({
        useAiChatbot: this.useAiChatbot,
        companyId: this.aiChatbotCompanyId,
        jobId: this.jobId,
        currentAccountId: this.currentAccountId,
        locale: this.$i18n && this.$i18n.locale
      })
    }
  },
  mounted () {
    window.addEventListener('focus', this.mountOrUpdateAiChatbot)
  },
  beforeDestroy () {
    window.removeEventListener('focus', this.mountOrUpdateAiChatbot)
    this.clearAiChatbotTriggerTimer()
    this.unmountAiChatbot()
  },
  methods: {
    ...mapActions('aiChatbot', [
      'getPublicAiChatbotConfig',
      'getPublicAiChatbotWelcomeTrigger',
      'getPublicAiChatbotWelcomeBackTrigger',
      'getPublicAiChatbotStay30sTrigger'
    ]),
    clearAiChatbotTriggerTimer () {
      if (this.aiChatbotStay30sTimer) {
        clearTimeout(this.aiChatbotStay30sTimer)
        this.aiChatbotStay30sTimer = null
      }
    },
    showAiChatbotTrigger (payload) {
      if (payload?.available === false) {
        this.setAiChatbotUnavailable(payload.reason)
        return false
      }
      if (!payload || payload.should_trigger === false) {
        return false
      }
      const chatbotIsOpen = Boolean(this.aiChatbotInstance && this.aiChatbotInstance.isOpen && this.aiChatbotInstance.isOpen())
      if (!payload || !this.aiChatbotInstance || chatbotIsOpen) {
        return false
      }
      return Boolean(this.aiChatbotInstance.showTrigger && this.aiChatbotInstance.showTrigger(payload))
    },
    setAiChatbotUnavailable (reason) {
      this.aiChatbotAvailability = 'unavailable'
      this.aiChatbotUnavailableReason = reason
      this.unmountAiChatbot()
      this.aiChatbotPublicConfig = null
    },
    async syncAiChatbotTriggers () {
      this.clearAiChatbotTriggerTimer()
      if (!this.useAiChatbot || !this.aiChatbotInstance || !this.aiChatbotCompanyId) {
        return
      }

      const requestToken = this.aiChatbotTriggerRequestToken + 1
      this.aiChatbotTriggerRequestToken = requestToken
      try {
        if (!this.aiChatbotVisitorId) {
          const welcomeResp = await this.getPublicAiChatbotWelcomeTrigger({
            company_id: this.aiChatbotCompanyId,
            job_id: this.jobId
          })
          if (requestToken !== this.aiChatbotTriggerRequestToken) {
            return
          }
          await this.mountOrUpdateAiChatbot()
          this.showAiChatbotTrigger(welcomeResp)
        }
        if (this.jobId && this.aiChatbotVisitorId) {
          const welcomeBackResp = await this.getPublicAiChatbotWelcomeBackTrigger({
            company_id: this.aiChatbotCompanyId,
            visitor_id: this.aiChatbotVisitorId,
            job_id: this.jobId
          })
          if (requestToken !== this.aiChatbotTriggerRequestToken) {
            return
          }
          this.showAiChatbotTrigger(welcomeBackResp)
        }

        this.scheduleAiChatbotStay30sTrigger(requestToken)
      } catch (error) {
        if (requestToken === this.aiChatbotTriggerRequestToken) {
          this.setAiChatbotUnavailable('request_failed')
        }
      }
    },
    scheduleAiChatbotStay30sTrigger (requestToken) {
      this.clearAiChatbotTriggerTimer()
      const visitorId = this.aiChatbotVisitorId
      if (!this.aiChatbotInstance || !this.jobId || !visitorId || !this.aiChatbotCompanyId) {
        return
      }

      this.aiChatbotStay30sTimer = setTimeout(async () => {
        this.aiChatbotStay30sTimer = null
        try {
          const stay30sResp = await this.getPublicAiChatbotStay30sTrigger({
            company_id: this.aiChatbotCompanyId,
            visitor_id: visitorId,
            job_id: this.jobId
          })
          if (requestToken !== this.aiChatbotTriggerRequestToken) {
            return
          }
          if (!this.aiChatbotInstance || (this.aiChatbotInstance.isOpen && this.aiChatbotInstance.isOpen())) {
            return
          }
          this.showAiChatbotTrigger(stay30sResp)
        } catch (error) {
          if (requestToken === this.aiChatbotTriggerRequestToken) {
            this.setAiChatbotUnavailable('request_failed')
          }
        }
      }, STAY_TRIGGER_DELAY_MS)
    },
    async mountOrUpdateAiChatbot () {
      if (!this.useAiChatbot || !this.hasRequiredAiChatbotIdentity) {
        return
      }

      const target = this.$refs.aiChatbotRoot
      if (!target) {
        return
      }

      const requestToken = ++this.aiChatbotMountRequestToken
      this.aiChatbotAvailability = 'loading'
      try {
        const config = await this.getPublicAiChatbotConfig({ company_id: this.aiChatbotCompanyId })
        if (requestToken !== this.aiChatbotMountRequestToken) {
          return
        }
        if (!config.available) {
          this.setAiChatbotUnavailable(config.reason)
          return
        }
        this.aiChatbotPublicConfig = config
        const embedApi = await loadAiChatbotEmbedScript()
        if (requestToken !== this.aiChatbotMountRequestToken || !this.useAiChatbot || !this.$refs.aiChatbotRoot) {
          return
        }
        this.aiChatbotAvailability = 'available'
        this.aiChatbotUnavailableReason = null
        if (this.aiChatbotInstance) {
          embedApi.updateAiChatbot(target, this.aiChatbotEmbedOptions)
          return
        }
        this.aiChatbotInstance = embedApi.mountAiChatbot(target, this.aiChatbotEmbedOptions)
        this.aiChatbotInstance.on(ACTION_TRIGGER_EVENT_APPLY_JOB, () => {
          this.handleApplyJob()
        })
        await this.syncAiChatbotTriggers()
      } catch (error) {
        if (requestToken === this.aiChatbotMountRequestToken) {
          this.setAiChatbotUnavailable('request_failed')
        }
      }
    },
    unmountAiChatbot () {
      this.aiChatbotMountRequestToken += 1
      this.clearAiChatbotTriggerTimer()
      this.aiChatbotTriggerRequestToken += 1
      const target = this.$refs.aiChatbotRoot
      const embedApi = getAiChatbotEmbedApi()
      if (target && embedApi && embedApi.unmountAiChatbot) {
        embedApi.unmountAiChatbot(target)
      } else if (this.aiChatbotInstance && this.aiChatbotInstance.unmountAiChatbot) {
        this.aiChatbotInstance.unmountAiChatbot()
      }

      this.aiChatbotInstance = null
    },
    handleApplyJob () {
      this.$emit('applyJob')
    }
  },
  watch: {
    useAiChatbot: {
      immediate: true,
      handler (value) {
        if (value) {
          this.$nextTick(() => {
            this.mountOrUpdateAiChatbot()
          })
          return
        }

        this.unmountAiChatbot()
      }
    },
    aiChatbotEmbedOptionsKey () {
      if (this.aiChatbotAvailability === 'unavailable' || !this.useAiChatbot) {
        return
      }

      this.$nextTick(() => {
        this.mountOrUpdateAiChatbot()
      })
    },
    aiChatbotTriggerContextKey () {
      if (this.aiChatbotAvailability === 'unavailable' || !this.useAiChatbot) {
        return
      }

      this.$nextTick(() => {
        this.syncAiChatbotTriggers()
      })
    },
    aiChatbotCompanyId () {
      this.aiChatbotPublicConfig = null

      if (!this.useAiChatbot) {
        return
      }

      this.$nextTick(() => {
        this.mountOrUpdateAiChatbot()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.chatbot-adapter {
  min-height: 0;

  &.is-ai-chatbot {
    position: relative;
    z-index: 99999;
  }
}

.ai-chatbot-embed-root {
  min-height: 0;
}
</style>
